/**
 * Bridge Title Modal Component
 * Allows users to customize the title before saving to clipboard manager
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';

interface BridgeTitleModalProps {
  isOpen: boolean;
  defaultTitle: string;
  messageContent: string;
  onSave: (title: string) => void;
  onCancel: () => void;
}

export default function BridgeTitleModal({ isOpen, defaultTitle, messageContent, onSave, onCancel }: BridgeTitleModalProps) {
  const [title, setTitle] = useState(defaultTitle);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update title when defaultTitle changes
  useEffect(() => {
    setTitle(defaultTitle);
  }, [defaultTitle]);

  // Auto-focus and select text when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure the modal is rendered
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    onSave(title.trim() || defaultTitle); // Use default if empty
  };

  const handleCopyToClipboard = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await navigator.clipboard.writeText(messageContent);
      toast.success('Copied to clipboard!');
      onCancel(); // Close modal immediately
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy to clipboard');
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-dark-card border border-dark-border rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold mb-4 text-dark-text">Save to Bridge</h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label htmlFor="bridge-title" className="block text-sm font-medium text-dark-text-muted mb-2">
              Title
            </label>
            <input
              ref={inputRef}
              id="bridge-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-md text-dark-text focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent"
              placeholder="Enter a title for this item..."
            />
          </div>

          <div className="flex gap-3 justify-between">
            <button
              type="button"
              onClick={handleCopyToClipboard}
              disabled={isSubmitting}
              className="px-4 py-2 bg-accent-blue hover:bg-accent-blue/80 text-white rounded-md transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
              </svg>
              Send to Clipboard
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 bg-dark-bg hover:bg-dark-border text-dark-text rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-accent-orange hover:bg-accent-orange/80 text-white rounded-md transition-colors disabled:opacity-50"
              >
                Save to Bridge
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
